import { push } from 'react-router-redux'

import { actionTypes } from 'actions/actionTypes'
import { basketPostcodeChange } from 'actions/basket'
import { featuresSet } from 'actions/features'
import loginActions from 'actions/login'
import { menuLoadBoxPrices } from 'actions/menu'
import { redirect } from 'actions/redirect'
import { trackUTMAndPromoCode } from 'actions/tracking'
import {
  completeWizardPostcode,
  clickSeeThisWeeksMenu,
  signupSocialBelongingBanner,
  signupCheckAccountNewCustomer,
  signupCheckAccountExistingCustomer,
  signupApplyVoucher,
} from 'actions/trackingKeys'
import * as trackingKeys from 'actions/trackingKeys'
import { fetchCountByPostcode } from 'apis/signup'
import routes, { client } from 'config/routes'
import { signupConfig } from 'routes/Signup/signupConfig'
import { stepByName } from 'routes/Signup/signupUtils'
import { getAccessToken } from 'selectors/auth'
import { getUTMAndPromoCode } from 'selectors/tracking'
import logger from 'utils/logger'

export function signupStepsReceive(stepNames) {
  return {
    type: actionTypes.SIGNUP_STEPS_RECEIVE,
    stepNames,
  }
}

export function signupSetStep(step) {
  return (dispatch, getState) => {
    const signupState = getState().signup
    const stepNames = signupState.getIn(['wizard', 'stepNames'])

    if (step) {
      const newStepNumber = stepNames.findIndex((stepName) => stepName === step.get('name'))
      const isLastStep = newStepNumber === stepNames.size - 1

      dispatch({
        type: actionTypes.SIGNUP_STEP_SET,
        currentStepName: step.get('name'),
        currentStepNumber: newStepNumber,
        isLastStep,
        trackingData: {
          type: actionTypes.SIGNUP_STEP_SET,
          step: step.get('slug'),
          stepName: step.get('name'),
        },
      })
    }
  }
}

export function signupCookForKidsChange(cookForKids) {
  return (dispatch) => {
    dispatch({
      type: actionTypes.SIGNUP_COOK_FOR_KIDS,
      cookForKids,
      trackingData: {
        type: actionTypes.SIGNUP_COOK_FOR_KIDS,
        cookForKids,
      },
    })
  }
}

export function signupTracking() {
  return (dispatch, getState) => {
    const { basket } = getState()
    const postcode = basket.get('postcode')
    const numAdults = basket.get('numAdults')
    const numPortions = basket.get('numPortions')
    const date = basket.get('date')
    const slotId = basket.get('slotId')

    dispatch({
      type: actionTypes.SIGNUP_TRACKING,
      trackingData: {
        actionType: actionTypes.SIGNUP_TRACKING,
        postcode,
        numAdults,
        numPortions,
        date,
        slotId,
      },
    })
  }
}

/**
 * Redirects application to Signup exact step.
 */
export function signupNextStep(stepName) {
  return (dispatch, getState) => {
    const step = stepByName(stepName)
    if (step) {
      const state = getState()
      const signupState = state.signup
      const isCurrentlyTheLastStep = signupState.getIn(['wizard', 'isLastStep'])
      const lastWizardStep = signupState.getIn(['wizard', 'stepNames']).last()
      const slug = step.get('slug')
      if (isCurrentlyTheLastStep && (step.get('name') === lastWizardStep || !slug)) {
        dispatch(signupTracking())

        const path = `${routes.client.signup}/${signupConfig.sellThePropositionPageSlug}`

        return dispatch(redirect(path))
      }

      try {
        const { search } = getState().routing.locationBeforeTransitions
        dispatch(push(`${client.signup}/${slug}${search}`))
      } catch (e) {
        dispatch(push(`${client.signup}/${slug}`))
      }
    }

    return null
  }
}

export function signupGoToMenu() {
  return (dispatch) => {
    dispatch(trackUTMAndPromoCode(clickSeeThisWeeksMenu))
    dispatch(redirect(routes.client.menu))
    dispatch({ type: actionTypes.WIZARD_SEEN })
  }
}

export const trackSignupWizardAction =
  (type, additionalData = {}) =>
  (dispatch, getState) => {
    const { promoCode, UTM } = getUTMAndPromoCode(getState())

    dispatch({
      type,
      trackingData: {
        actionType: type,
        ...UTM,
        promoCode,
        ...additionalData,
      },
    })
  }

export const signupSetSocialBelongingOptions = (socialBelongingOptions) => ({
  type: actionTypes.SIGNUP_SET_SOCIAL_BELONGING_OPTIONS,
  ...socialBelongingOptions,
})

export const signupGetCountByPostcode = (postcode) => async (dispatch, getState) => {
  let socialBelongingOptions = {}
  try {
    const accessToken = getAccessToken(getState())
    const { data } = await fetchCountByPostcode(accessToken, { postcode })
    socialBelongingOptions = data
  } catch (e) {
    logger.error('No users found in current area')
  } finally {
    dispatch(signupSetSocialBelongingOptions(socialBelongingOptions))
  }
}

export function signupChangePostcode(postcode, nextStepName) {
  return async (dispatch, getState) => {
    await dispatch(basketPostcodeChange(postcode))

    if (!getState().error.get(actionTypes.BOXSUMMARY_DELIVERY_DAYS_RECEIVE, false)) {
      dispatch(trackSignupWizardAction(completeWizardPostcode, { postcode }))
      signupNextStep(nextStepName)(dispatch, getState)
    }
  }
}

export const signupDismissDiscountAppliedBar = () => ({
  type: actionTypes.SIGNUP_DISMISS_DISCOUNT_APPLIED_BAR,
})

export const trackSocialBelongingBannerAppearance = () => (dispatch, getState) => {
  const state = getState()
  const { promoCode, UTM } = getUTMAndPromoCode(state)
  const district = state.signup.getIn(['wizard', 'district'])
  const amountOfCustomers = state.signup.getIn(['wizard', 'amountOfCustomers'])

  dispatch({
    type: signupSocialBelongingBanner,
    trackingData: {
      actionType: signupSocialBelongingBanner,
      ...UTM,
      promo_code: promoCode,
      district,
      number_of_customers: amountOfCustomers,
    },
  })
}

export const signupSetGoustoOnDemandEnabled = (featureValue) => (dispatch) => {
  dispatch(featuresSet([{ feature: 'isGoustoOnDemandEnabled', value: featureValue }]))
}

export const trackCuisineSelected = (cuisineName) => (dispatch) => {
  const type = trackingKeys.cuisineSelected

  dispatch({
    type: actionTypes.TRACKING,
    trackingData: {
      actionType: type,
      cuisineName,
    },
  })
}

export const trackCuisineDeselected = (cuisineName) => (dispatch) => {
  const type = trackingKeys.cuisineDeselected

  dispatch({
    type: actionTypes.TRACKING,
    trackingData: {
      actionType: type,
      cuisineName,
    },
  })
}

export const trackSignupPersonalisationComplete = (selectedCuisines) => (dispatch) => {
  const type = trackingKeys.signupPersonalisationComplete

  dispatch({
    type: actionTypes.TRACKING,
    trackingData: {
      actionType: type,
      selectedCuisines: [...selectedCuisines],
    },
  })
}

export const signupCheckAccountGoToBoxPrices = () => (dispatch) => {
  dispatch(menuLoadBoxPrices())
  dispatch(trackUTMAndPromoCode(signupCheckAccountNewCustomer))
  dispatch(redirect(`${routes.client.signup}/box-size`))
}

export const signupCheckAccountLogin = () => (dispatch) => {
  dispatch(trackUTMAndPromoCode(signupCheckAccountExistingCustomer))

  const { loginVisibilityChange } = loginActions
  dispatch(loginVisibilityChange(true))
}

export const signupApplyVoucherGoToDeliveries = () => (dispatch) => {
  dispatch(trackUTMAndPromoCode(signupApplyVoucher))
  dispatch(redirect(`${routes.client.myDeliveries}`))
}
